/**
 * Berechnung der Beträge eines Angebots (KON-098). Eine reine Funktion, die Oberfläche, Server und PDF gemeinsam nutzen, damit überall dasselbe herauskommt.
 *
 * Gerechnet wird in ganzen Cent mit BigInt, Mengen in Tausendsteln, Rabatte in Basispunkten (1 % = 100). Gerundet wird kaufmännisch (bei ,5 vom Nullpunkt weg)
 * und zwar je Position, je Steuersatz nach dem Gesamtrabatt und je Steuersatz bei der Steuer. Das ist die übliche Reihenfolge auf deutschen Belegen: Die Summe
 * der Positionen stimmt mit der Netto-Zwischensumme überein, und die Steuer wird je Satz einmal gerundet, nicht je Position.
 */
export const TAX_RATES = [19, 7, 0];
/** Ganzzahlige Division mit kaufmännischer Rundung, auch für negative Zähler */
function roundDiv(a, b) {
    const negative = a < 0n !== b < 0n;
    const absA = a < 0n ? -a : a;
    const absB = b < 0n ? -b : b;
    const q = (absA * 2n + absB) / (absB * 2n);
    return negative ? -q : q;
}
/** Betrag einer Position in Cent: Menge mal Einzelpreis, abzüglich Rabatt */
export function lineAmountCents(line) {
    const raw = BigInt(Math.round(line.quantityMilli)) * BigInt(Math.round(line.unitPriceCents)) * BigInt(10000 - Math.round(line.discountBp));
    return Number(roundDiv(raw, 1000n * 10000n));
}
/**
 * Summen eines Angebots. `smallBusiness`: Kleinunternehmer nach § 19 UStG weisen keine Steuer aus, alle Positionen gelten dann mit 0 %.
 */
export function offerTotals(lines, discountBp = 0, smallBusiness = false) {
    const lineCents = lines.map(lineAmountCents);
    const byRate = new Map();
    lines.forEach((l, i) => {
        const rate = smallBusiness ? 0 : l.taxRate;
        byRate.set(rate, (byRate.get(rate) ?? 0n) + BigInt(lineCents[i]));
    });
    const factor = BigInt(10000 - Math.round(discountBp));
    const groups = [...byRate.entries()]
        .sort((a, b) => b[0] - a[0])
        .map(([rate, sum]) => {
        const net = roundDiv(sum * factor, 10000n);
        const tax = roundDiv(net * BigInt(rate), 100n);
        return { rate, linesCents: Number(sum), netCents: Number(net), taxCents: Number(tax) };
    });
    const linesCents = groups.reduce((s, g) => s + g.linesCents, 0);
    const netCents = groups.reduce((s, g) => s + g.netCents, 0);
    const taxCents = groups.reduce((s, g) => s + g.taxCents, 0);
    return { lineCents, linesCents, discountCents: linesCents - netCents, netCents, taxCents, grossCents: netCents + taxCents, groups };
}
const euro = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
/** 123456 → „1.234,56 €“ */
export const formatCents = (cents) => euro.format(cents / 100);
/** Menge aus Tausendsteln zur Anzeige: 1500 → „1,5“ */
export const formatQuantity = (milli) => new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 }).format(milli / 1000);
/** Prozent aus Basispunkten zur Anzeige: 1250 → „12,5 %“ */
export const formatPercent = (bp) => `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(bp / 100)} %`;
//# sourceMappingURL=calc.js.map